'use client';

import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Snackbar, SnackbarOrigin, Fade } from '@mui/material';
import { XCircle, AlertTriangle, Info } from 'lucide-react';

interface SnackbarMessage {
  message: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  position?: SnackbarOrigin;
}

interface SnackbarContextValue {
  showSnackbar: (options: SnackbarMessage | string) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [duration, setDuration] = useState(3000);
  const [position, setPosition] = useState<SnackbarOrigin>({
    vertical: 'top',
    horizontal: 'center',
  });

  const showSnackbar = (options: SnackbarMessage | string) => {
    if (typeof options === 'string') {
      setMessage(options);
      setSeverity('success');
      setDuration(3000);
      setPosition({ vertical: 'top', horizontal: 'center' });
    } else {
      setMessage(options.message);
      setSeverity(options.severity || 'success');
      setDuration(options.duration || 3000);
      setPosition(options.position || { vertical: 'top', horizontal: 'center' });
    }
    setOpen(true);
  };

  const showSuccess = useCallback((message: string) => {
    showSnackbar({ message, severity: 'success' });
  }, []);

  const showError = (message: string) => {
    showSnackbar({ message, severity: 'error' });
  };

  const showWarning = (message: string) => {
    showSnackbar({ message, severity: 'warning' });
  };

  const showInfo = (message: string) => {
    showSnackbar({ message, severity: 'info' });
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const getIcon = () => {
    switch (severity) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <Info size={20} />;
    }
  };

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={position}
        TransitionComponent={Fade}
        sx={{
          top: '40px',
          backgroundColor: '#262627',
          borderWidth: 1,
          borderColor: '#464646',
          borderRadius: '12px',
        }}
      >
        <div
          className={`min-w-[120px] max-w-[560px] px-4 py-2.5 flex items-center gap-2 text-[14px] text-white`}
        >
          {/* Icon */}
          <div className="flex-shrink-0">{getIcon()}</div>

          {/* Message */}
          <div className="flex-1 text-[14px] font-medium leading-[20px]">{message}</div>
        </div>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}

const SuccessIcon = () => {
  return (
    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect y="0.5" width="16" height="16" rx="8" fill="#0ABF52" />
      <path
        d="M3.66675 8.5L6.33341 11.1667L11.6667 5.83334"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

const WarningIcon = () => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.66602 9.99996C1.66602 14.5916 5.40768 18.3333 9.99935 18.3333C14.591 18.3333 18.3327 14.5916 18.3327 9.99996C18.3327 5.40829 14.591 1.66663 9.99935 1.66663C5.40768 1.66663 1.66602 5.40829 1.66602 9.99996ZM9.99935 6.04163C10.341 6.04163 10.6243 6.32496 10.6243 6.66663V10.8333C10.6243 11.175 10.341 11.4583 9.99935 11.4583C9.65768 11.4583 9.37435 11.175 9.37435 10.8333V6.66663C9.37435 6.32496 9.65768 6.04163 9.99935 6.04163ZM10.591 13.925C10.5077 14 10.416 14.0583 10.316 14.1C10.216 14.1416 10.1077 14.1666 9.99935 14.1666C9.89102 14.1666 9.78268 14.1416 9.68268 14.1C9.58268 14.0583 9.49102 14 9.40768 13.925C9.33268 13.8416 9.27435 13.7583 9.23268 13.65C9.19102 13.55 9.16602 13.4416 9.16602 13.3333C9.16602 13.225 9.19102 13.1166 9.23268 13.0166C9.27435 12.9166 9.33268 12.825 9.40768 12.7416C9.49102 12.6666 9.58268 12.6083 9.68268 12.5666C9.88268 12.4833 10.116 12.4833 10.316 12.5666C10.416 12.6083 10.5077 12.6666 10.591 12.7416C10.666 12.825 10.7243 12.9166 10.766 13.0166C10.8077 13.1166 10.8327 13.225 10.8327 13.3333C10.8327 13.4416 10.8077 13.55 10.766 13.65C10.7243 13.7583 10.666 13.8416 10.591 13.925Z"
        fill="#FC9700"
      />
    </svg>
  );
};

const ErrorIcon = () => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.66602 9.99996C1.66602 14.5916 5.40768 18.3333 9.99935 18.3333C14.591 18.3333 18.3327 14.5916 18.3327 9.99996C18.3327 5.40829 14.591 1.66663 9.99935 1.66663C5.40768 1.66663 1.66602 5.40829 1.66602 9.99996ZM12.7993 12.8C12.6743 12.925 12.516 12.9833 12.3577 12.9833C12.1993 12.9833 12.041 12.925 11.916 12.8L9.99935 10.8833L8.08268 12.8C7.95768 12.925 7.79935 12.9833 7.64102 12.9833C7.48268 12.9833 7.32435 12.925 7.19935 12.8C6.95768 12.5583 6.95768 12.1583 7.19935 11.9166L9.11602 9.99996L7.19935 8.08329C6.95768 7.84163 6.95768 7.44163 7.19935 7.19996C7.44102 6.95829 7.84102 6.95829 8.08268 7.19996L9.99935 9.11663L11.916 7.19996C12.1577 6.95829 12.5577 6.95829 12.7993 7.19996C13.041 7.44163 13.041 7.84163 12.7993 8.08329L10.8827 9.99996L12.7993 11.9166C13.041 12.1583 13.041 12.5583 12.7993 12.8Z"
        fill="#EC4B2B"
      />
    </svg>
  );
};
